/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2023 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import PropTypes from "prop-types";
import React, { useEffect } from "react";
import { SubMenu } from "@react/react-spectrum/Menu";
import { injectIntl } from "react-intl";
import { ContextMenuItemContainer } from "containers/contextMenu/ContextMenuItemContainer";
import CustomIcon from "dc-preview-utils/src/components/CustomIcon";
import ContextMenuCss from "./css/ContextMenu.css";
import PremiumIndicator from "./PremiumIndicator";
import { PREMIUM_ICON_TOOLTIP_ID } from "constants/constants";

const SubMenuChildrenWrapper = ({ children, mouseEvents }) => {
  useEffect(() => {
    mouseEvents?.onSubmenuOpen?.();
  }, []);

  return children;
};

const ContextMenuGroupItem = props => {
  const { contextMenuItemStyle, menuItem } = props;
  const { title, icon, children, mouseEvents, shouldShowPremiumIcon, premiumIconTooltip } = menuItem;

  let style = { whiteSpace: "nowrap" };

  const getChildren = () =>
    children.map((item, idx) => (
      <ContextMenuItemContainer key={idx} menuItem={item} formatMessage={props.intl.formatMessage} />
    ));

  const onContextMenu = eventObj => {
    if (eventObj.stopPropagation) {
      eventObj.stopPropagation();
      eventObj.preventDefault();
    }
  };

  const onClickHandler = eventObj => {
    if (eventObj.stopPropagation) {
      eventObj.stopPropagation();
      eventObj.preventDefault();
    }
  };

  return (
    <div onClick={onClickHandler}>
      <SubMenu
        label={
          <div className={ContextMenuCss.contextMenuItemBody}>
            {props.intl.formatMessage({ id: title })}
            {shouldShowPremiumIcon && (
              <PremiumIndicator
                tooltip={premiumIconTooltip ?? props.intl.formatMessage({ id: PREMIUM_ICON_TOOLTIP_ID })}
              />
            )}
          </div>
        }
        {...(icon ? { icon: <CustomIcon url={icon} /> } : {})}
        style={{ ...style, ...contextMenuItemStyle }}
        onContextMenu={onContextMenu}
        className={ContextMenuCss.contextMenuItem}
      >
        <SubMenuChildrenWrapper mouseEvents={mouseEvents}>{getChildren()}</SubMenuChildrenWrapper>
      </SubMenu>
    </div>
  );
};

ContextMenuGroupItem.propTypes = {
  contextMenuItemStyle: PropTypes.object,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func
  }).isRequired,
  menuItem: PropTypes.shape({
    children: PropTypes.array.isRequired,
    icon: PropTypes.element,
    title: PropTypes.string.isRequired,
    shouldShowPremiumIcon: PropTypes.bool
  }).isRequired
};

ContextMenuGroupItem.defaultProps = {
  contextMenuItemStyle: {}
};

const injectedContextMenuGroupItem = injectIntl(ContextMenuGroupItem);

export { injectedContextMenuGroupItem };
